import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, Skeleton, Tabs } from 'antd';
import { ArrowRightOutlined, ReloadOutlined } from '@ant-design/icons';
import type { InvestorAnalyticsTab, OnboardingCohortRange, OnboardingPeriodPreset } from '@mygate/common';
import { AnalyticsOnboarding } from '@/components/analytics/AnalyticsOnboarding';
import { OnboardingPeriodPicker } from '@/components/OnboardingPeriodPicker';
import { AnalyticsInvestments } from '@/components/analytics/AnalyticsInvestments';
import { formatUtcDateTime, formatTime } from '@/components/analytics/AnalyticsPrimitives';
import { getAnalyticsAnchor, getOnboardingPresetRange } from '@/utils/onboardingPeriod';
import { useInvestmentAnalytics, useOnboardingAnalytics } from '@/hooks/useAnalytics';
import './Analytics.css';

const tabs = [
  { key: 'onboarding', label: 'Onboarding' },
  { key: 'investments', label: 'Investments' },
] satisfies Array<{ key: InvestorAnalyticsTab; label: string }>;
const rollingPresets: Exclude<OnboardingPeriodPreset, 'CUSTOM'>[] = ['DAY', 'WEEK', 'MONTH'];

export function Analytics() {
  const [search, setSearch] = useSearchParams();
  const tab = tabs.find(item => item.key === search.get('tab'))?.key ?? 'onboarding';
  const [anchor, setAnchor] = useState(() => getAnalyticsAnchor(new Date()));
  const [range, setRange] = useState<OnboardingCohortRange>(() => getOnboardingPresetRange('MONTH', anchor));
  const investments = useInvestmentAnalytics(tab === 'investments');
  const onboarding = useOnboardingAnalytics(range, tab === 'onboarding');
  const query = tab === 'investments' ? investments : onboarding;
  // A failed refresh must never make a previously cached response look like a new measurement.
  const data = query.isError ? undefined : query.data;
  const refresh = () => {
    if (tab === 'investments') {
      void query.refetch();
      return;
    }
    const preset = rollingPresets.find(key => {
      const candidate = getOnboardingPresetRange(key, anchor);
      return candidate.from === range.from && candidate.to === range.to;
    });
    const nextAnchor = getAnalyticsAnchor(new Date());
    setAnchor(nextAnchor);
    if (preset && nextAnchor !== anchor) setRange(getOnboardingPresetRange(preset, nextAnchor));
    else void query.refetch();
  };
  const changeTab = (key: string) =>
    setSearch(previous => {
      const next = new URLSearchParams(previous);
      next.set('tab', key);
      return next;
    });

  return (
    <div className="analytics-page">
      <Tabs
        activeKey={tab}
        onChange={changeTab}
        items={tabs.map(item => ({ ...item, disabled: query.isFetching && item.key !== tab }))}
        className="analytics-tabs"
        tabBarExtraContent={
          <Link to="/dashboard">
            Dashboard <ArrowRightOutlined />
          </Link>
        }
      />
      <div className="analytics-toolbar">
        {tab === 'onboarding' ? (
          <div>
            <OnboardingPeriodPicker
              key={`${range.from}/${range.to}`}
              range={range}
              anchor={anchor}
              onChange={setRange}
              label="Analytics-Zeitraum"
              disabled={query.isFetching}
            />
            <p>
              {formatUtcDateTime(range.from)} – {formatUtcDateTime(range.to)} UTC
            </p>
          </div>
        ) : (
          <p>
            {!investments.isError &&
              investments.data?.coverage.newestSnapshotAt &&
              `Bestände vom ${formatTime(investments.data.coverage.newestSnapshotAt)} (Berlin)`}
          </p>
        )}
        <Button
          icon={<ReloadOutlined />}
          loading={query.isFetching}
          onClick={refresh}
          title="Auswertungen werden bis zu fünf Minuten wiederverwendet. Der Datenstand bleibt sichtbar."
        >
          Aktualisieren
        </Button>
      </div>
      {query.isPending && (
        <div aria-live="polite">
          <Skeleton active paragraph={{ rows: 8 }} />
          <p>Analytics-Daten werden ausgewertet …</p>
        </div>
      )}
      {query.isError && (
        <Alert
          type="error"
          showIcon
          message="Analytics konnten nicht geladen werden."
          description={query.error.message}
          action={<Button onClick={refresh}>Erneut versuchen</Button>}
        />
      )}
      {data && (
        <>
          <div className="analytics-measured">
            <span>Stand {formatTime(data.measuredAt)} (Berlin)</span>
          </div>
          {tab === 'investments' && investments.data && <AnalyticsInvestments data={investments.data} />}
          {tab === 'onboarding' && onboarding.data && (
            <AnalyticsOnboarding key={`${range.from}/${range.to}`} data={onboarding.data} />
          )}
        </>
      )}
    </div>
  );
}
